import { Textarea } from '@atlas/ui-primitives'
import { type KeyboardEvent, useId, useRef, useState } from 'react'
import type { PromptVariable } from '@/domain/prompt.types'
import { PROMPT_VARIABLE_TYPE_LABEL } from '@/domain/prompt.types'
import { PlaceholderMenu } from './PlaceholderMenu'
import { usePlaceholderCompletion } from './usePlaceholderCompletion'

/**
 * The body textarea, with completion for the variables the prompt declares.
 *
 * Typing `{{` is the trigger. Without it an author had to remember every key exactly
 * — a typo produced an undeclared placeholder that blocked save, and the only way to
 * check a name was to read the variables panel and type it again by hand.
 *
 * Completion is offered from the DECLARED variables rather than free text, so the
 * list is exactly the set that will render; anything else is the lint's problem.
 */
export function PromptBodyEditor({
	body,
	variables,
	onChange,
}: {
	body: string
	variables: PromptVariable[]
	onChange: (next: string) => void
}) {
	const wrap = useRef<HTMLDivElement>(null)
	const menuId = useId()
	const [caretAt, setCaretAt] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

	const completion = usePlaceholderCompletion(variables.map((v) => v.key))
	const typeOf = new Map(variables.map((v) => [v.key, PROMPT_VARIABLE_TYPE_LABEL[v.type]]))

	/**
	 * The textarea itself.
	 *
	 * Queried rather than held by ref: the `Textarea` primitive does not forward one,
	 * and widening its props for this single caller would push a concern onto every
	 * other consumer of the design system.
	 */
	const field = () => wrap.current?.querySelector('textarea') ?? null

	const accept = (key: string) => {
		const node = field()
		const caret = node?.selectionEnd ?? body.length
		const next = completion.accept(body, caret, key)

		onChange(next.text)
		requestAnimationFrame(() => node?.setSelectionRange(next.caret, next.caret))
	}

	const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (completion.matches.length === 0) return

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			completion.move(event.key === 'ArrowDown' ? 1 : -1)
			return
		}

		// Enter and Tab both accept, because both read as "take the suggestion" and
		// Enter alone would otherwise insert a newline mid-placeholder.
		if (event.key === 'Enter' || event.key === 'Tab') {
			event.preventDefault()
			accept(completion.matches[completion.active] as string)
			return
		}

		if (event.key === 'Escape') completion.dismiss()
	}

	return (
		<div ref={wrap} className="relative">
			<Textarea
				rows={12}
				value={body}
				aria-label="Prompt body"
				className="font-mono"
				// Combobox semantics while the completion list is open, so a screen reader
				// announces that suggestions appeared and which row Enter would insert. The
				// keyboard handling was already right; nothing was telling a reader about it.
				role="combobox"
				aria-expanded={completion.matches.length > 0}
				aria-controls={completion.matches.length > 0 ? menuId : undefined}
				aria-activedescendant={completion.matches.length > 0 ? `${menuId}-option-${completion.active}` : undefined}
				aria-autocomplete="list"
				onKeyDown={onKeyDown}
				onBlur={() => completion.dismiss()}
				onChange={(e) => {
					const { value, selectionEnd } = e.target
					onChange(value)

					// The caret is read from the event target, but a CONTROLLED textarea may
					// have `selectionEnd` at 0 in some browsers until React commits the value.
					// Falling back to the typed length keeps the trigger working there — the
					// caret is at the end for ordinary typing, which is when this fires.
					const caret = selectionEnd > 0 ? selectionEnd : value.length
					completion.sync(value, caret)

					const node = field()
					if (node !== null) setCaretAt(caretPosition(node, caret))
				}}
			/>

			<PlaceholderMenu
				id={menuId}
				matches={completion.matches}
				active={completion.active}
				hintFor={(key) => typeOf.get(key)}
				position={caretAt}
				onAccept={accept}
			/>
		</div>
	)
}

/**
 * Where the caret sits inside the textarea, in pixels relative to the field.
 *
 * A textarea exposes no caret geometry, so the text BEFORE the caret is rendered
 * into an offscreen mirror that copies the field's own font, padding, width and
 * wrapping. The end of that text is then the caret's position — the same technique
 * an @mention menu uses, and the reason the menu can follow the cursor instead of
 * anchoring under the whole control.
 */
function caretPosition(field: HTMLTextAreaElement, index: number): { top: number; left: number } {
	const style = window.getComputedStyle(field)
	const mirror = document.createElement('div')

	// Copy every property that affects where a glyph lands; anything missed shows up
	// as the menu drifting away from the caret on long or wrapped lines.
	for (const property of [
		'boxSizing',
		'fontFamily',
		'fontSize',
		'fontStyle',
		'fontWeight',
		'letterSpacing',
		'lineHeight',
		'paddingBottom',
		'paddingLeft',
		'paddingRight',
		'paddingTop',
		'textTransform',
		'width',
		'wordSpacing',
	] as const) {
		mirror.style[property] = style[property]
	}

	mirror.style.position = 'absolute'
	mirror.style.visibility = 'hidden'
	mirror.style.whiteSpace = 'pre-wrap'
	mirror.style.overflowWrap = 'break-word'
	mirror.textContent = field.value.slice(0, index)

	// A zero-width marker at the caret: an empty element has no box to measure, and a
	// trailing newline would otherwise collapse.
	const marker = document.createElement('span')
	marker.textContent = '​'
	mirror.appendChild(marker)

	document.body.appendChild(mirror)
	const top = marker.offsetTop - field.scrollTop
	const left = marker.offsetLeft
	document.body.removeChild(mirror)

	// One line below the caret, so the menu never covers the text being typed.
	return { top: top + Number.parseFloat(style.lineHeight || '16'), left }
}
